export const ApckConfirmMultiCheckDialog = {
    props: {
        message: {
            type: String,
            default: '',
        },
        /**
         * @type {{ key: string, label: string, default?: boolean }[]}
         * Each entry renders one checkbox. acceptCallback receives { [key]: boolean, ... }.
         */
        checks: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        const values = {};
        for (const c of this.checks) {
            values[c.key] = c.default ?? false;
        }
        return { values };
    },
    methods: {
        accept() {
            this.$emit('accept', { ...this.values });
            this.$emit('close');
        },
        deny() {
            this.$emit('deny');
            this.$emit('close');
        },
    },
    template: `
        <div class="alert-dialog">
            <p>{{message}}</p>
            <hr />
            <apck-field v-for="c in checks" :key="c.key">
                <apck-checkbox
                    v-model="values[c.key]"
                    :label="c.label"
                />
            </apck-field>
            <div class="pull-right pb-1">
                <span class="btn-positive btn-short btn" v-on:click="accept">OK</span>
                <span class="btn-negative btn-short btn" v-on:click="deny">Cancel</span>
            </div>
        </div>
    `
}

export const ApckConfirmCheckDialog = {
    // TODO: styles
    props: {
        message: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        defaultCheckboxValue: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            checkboxValue: this.defaultCheckboxValue,
        }
    },
    methods: {
        accept() {
            this.$emit('accept', this.checkboxValue);
            this.$emit('close');
        },
        deny() {
            this.$emit('deny');
            this.$emit('close');
        }
    },
    template: `
        <div class="alert-dialog">
            <p>{{message}}</p>
            <hr />
            <apck-field>
                <apck-checkbox
                    v-model="checkboxValue"
                    :label="label"
                />
            </apck-field>
            <div class="pull-right pb-1">
                <span class="btn-positive btn-short btn" v-on:click="accept">OK</span>
                <span class="btn-negative btn-short btn" v-on:click="deny">Cancel</span>
            </div>
        </div>
    `
}


export const ApckConfirmDialog = {
    // TODO: styles
    props: {
        message: {
            type: String,
            default: '',
        },
        confirmWithInput: {
            type: Boolean,
            default: false,
        },
        confirmWithInputValue: {
            type: String,
            default: '',
        },
        confirmWithNote: {
            type: Boolean,
            default: false,
        },
        aboveNotesText: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            noteValue: '',
            errors: null,
            confirmValue: ''
        }
    },
    methods: {
        accept() {
            // 'Type: ' + Confirmation.input + ' to confirm'
            if (this.confirmWithInput) {
                if (this.confirmWithInputValue === this.confirmValue) {
                    this.$emit('accept');
                    this.$emit('close');
                    return;
                }
                const errorMessage = `Type: ${this.confirmWithInputValue} to confirm`;
                this.errors = [errorMessage];
                return;
            }
            if (this.confirmWithNote) {
                this.$emit('note', this.noteValue);
                this.$emit('close');
                return;
            }
            this.$emit('accept');
            this.$emit('close');
        },
        deny() {
            this.$emit('deny');
            this.$emit('close');
        },
        handleKeyPress(e) {
            if (e.key === 'Escape') {
                this.$emit('close');
                e.preventDefault();
            }
        }
    },
    mounted() {
      window.addEventListener('keydown', this.handleKeyPress);
    },
    beforeUnmount() {
      window.removeEventListener('keydown', this.handleKeyPress);
    },
    template: `
        <div class="alert-dialog">
            <p>{{message}}</p>
            <hr />
            <div v-if="confirmWithNote">
                <p>{{aboveNotesText}}</p>
                <apck-field>
                    <apck-text
                        v-model="noteValue"
                        :show-label="false"
                        rows="2"
                    />
                </apck-field>
            </div>
            <div v-if="confirmWithInput">
                <p>Type: <b>{{confirmWithInputValue}}</b> to confirm </p>
                <apck-field :errors="errors">
                    <apck-text
                        v-model="confirmValue"
                        :show-label="false"
                    />
                </apck-field>
            </div>
            <div class="pull-right pb-1">
                <span class="btn-positive btn-short btn" v-on:click="accept">OK</span>
                <span class="btn-negative btn-short btn" v-on:click="deny">Cancel</span>
            </div>
        </div>
    `
}

export const ApckConfirmScopeDialog = {
    // TODO: styles
    props: {
        confirmWithInput: {
            type: Boolean,
            default: false,
        },
        confirmWithInputValue: {
            type: String,
            default: '',
        },
        confirmWithNote: {
            type: Boolean,
            default: false,
        },
        aboveNotesText: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            noteValue: '',
            errors: null,
            confirmValue: ''
        }
    },
    methods: {
        accept() {
            // 'Type: ' + Confirmation.input + ' to confirm'
            if (this.confirmWithInput) {
                if (this.confirmWithInputValue === this.confirmValue) {
                    this.$emit('accept');
                    this.$emit('close');
                    return;
                }
                const errorMessage = `Type: ${this.confirmWithInputValue} to confirm`;
                this.errors = [errorMessage];
                return;
            }
            if (this.confirmWithNote) {
                this.$emit('note', this.noteValue);
                this.$emit('close');
                return;
            }
            this.$emit('accept');
            this.$emit('close');
        },
        deny() {
            this.$emit('deny');
            this.$emit('close');
        }
    },
    template: `
        <div>
            <hr />
            <p>When adding your scanning scope, please be reminded of the following:</p>
            <ul>
                <li><strong>Permission Required:</strong> You must only include assets and networks that you have explicit permission to scan. Unauthorised scanning of networks and systems is illegal and against our terms of service.</li>
                <li><strong>Responsibility:</strong> As the account holder, you are fully responsible for ensuring that all assets included in your scope are within your legal rights to scan. This responsibility includes any legal consequences resulting from unauthorised scans.</li>
                <li><strong>Non-Changeable Scope:</strong> Once set, your scanning scope cannot be modified until your license anniversary. Ensure your scope is accurate and complete before submission.</li>
                <li><strong>Compliance:</strong> Scanning assets without permission is a criminal offence. By submitting your scope, you agree to comply with all relevant laws and our terms and conditions.</li>
            </ul>
            <br />
            <p>If you have any questions or need assistance, please contact our support team.</p>
            <hr />
            <div v-if="confirmWithNote">
                <p>{{aboveNotesText}}</p>
                <apck-field>
                    <apck-text
                        v-model="noteValue"
                        :show-label="false"
                        rows="2"
                    />
                </apck-field>
            </div>
            <div v-if="confirmWithInput">
                <p>Type: <b>{{confirmWithInputValue}}</b> to confirm </p>
                <apck-field :errors="errors">
                    <apck-text
                        v-model="confirmValue"
                        :show-label="false"
                    />
                </apck-field>
            </div>
            <div class="pull-right">
                <span class="btn-positive btn-short btn" v-on:click="accept">I Accept</span>
                <span class="btn-negative btn-short btn" v-on:click="deny">Cancel</span>
            </div>
        </div>
    `
}
